import AddIcon from "@mui/icons-material/Add"
import RemoveIcon from "@mui/icons-material/Remove"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from "@mui/material"
import { FAQDto } from "api"
import { Desktop, Mobile } from "common/MediaQueries"
import { useState } from "react"
import styled from "styled-components"
import colors from "theme/colors"

const Wrapper = styled.div<{ $withMobilePadding?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  padding: 32px ${(p) => (p.$withMobilePadding ? "16px" : "0")};
  width: 100%;

  ${(p) => p.theme.breakpoints.up("md")} {
    gap: 40px;
    padding: 40px 0;
    max-width: 675px;
    margin: 0 auto;
  }
`

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  color: ${colors.black};
  max-width: 600px;
  ${(p) => p.theme.breakpoints.up("md")} {
    text-align: center;
    align-items: center;
  }
`

const Title = styled(Typography)`
  && {
    font-size: 24px;
    font-weight: 400;
    line-height: 35px;

    ${(p) => p.theme.breakpoints.up("md")} {
      font-size: 32px;
      line-height: 47px;
      text-align: center;
    }
  }
`

const Subtitle = styled(Typography).attrs({ variant: "body1" })`
  && {
    font-size: 18px;
    font-weight: 400;
    line-height: 1.3;
    max-width: 600px;

    ${(p) => p.theme.breakpoints.up("md")} {
      text-align: center;
      font-size: 24px;
      line-height: 35px;
    }
  }
`

const ItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 675px;
`

const StyledAccordion = styled(Accordion)`
  && {
    background-color: ${colors.white};
    border: none;
    border-radius: 0;
    box-shadow: none;
    margin: 0;
    width: 100%;
    padding: 0;

    &:not(:last-child) {
      border-bottom: 1px solid ${colors.platinum};
    }

    &.prev-to-expanded {
      border-bottom: none;
    }

    &.Mui-expanded {
      background-color: ${colors.lightGrey};
      border-bottom: none;
      margin: 0;
    }

    &:before {
      display: none;
    }
  }
`

const StyledAccordionSummary = styled(AccordionSummary)`
  && {
    background-color: transparent;
    min-height: 0;
    justify-content: flex-start;
    flex-direction: row-reverse;
    align-items: flex-start;
    padding: 16px 8px;
    gap: 12px;

    .MuiAccordionSummary-content {
      margin: 0;
      padding: 0;
      color: ${colors.black};
      text-align: right;

      &.Mui-expanded {
        margin: 0;
        padding: 0;
      }
    }

    &.Mui-expanded {
      min-height: 0;
      border-bottom: none;
    }

    .MuiAccordionSummary-expandIconWrapper {
      padding: 0;
      margin: 0;
      color: ${colors.black};
      transform: none;

      &.Mui-expanded {
        transform: none;
      }
    }
  }
`

const QuestionText = styled(Typography)<{ $expanded: boolean }>`
  && {
    font-size: 18px;
    font-weight: ${(p) => (p.$expanded ? 500 : 400)};
    line-height: 1.3;
    color: ${colors.black};
    text-align: right;
    flex: 1;
  }
`

const StyledAccordionDetails = styled(AccordionDetails)`
  && {
    padding: 0 44px 24px 8px;
  }
`

const AnswerText = styled(Typography).attrs({ variant: "body2" })`
  && {
    width: 100%;
    unicode-bidi: plaintext;
    color: ${colors.black};
    text-align: right;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    white-space: pre-line;
  }
`

type Props = {
  data: FAQDto
  withMobilePadding?: boolean
}

const FAQComp = ({ data, withMobilePadding }: Props) => {
  const [expanded, setExpanded] = useState<number | false>(false)

  if (!data?.isActive || !data?.faqLists?.length) return null

  const handleChange =
    (panel: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false)
    }

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.faqLists.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  }

  return (
    <Wrapper $withMobilePadding={withMobilePadding}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqJsonLd).replace(/</g, "\\u003c"),
        }}
      />
      <Heading>
        <Mobile>
          <Title variant="h3">{data.title}</Title>
        </Mobile>
        <Desktop>
          <Title variant="h2">{data.title}</Title>
        </Desktop>
        {data.subTitle && <Subtitle>{data.subTitle}</Subtitle>}
      </Heading>

      <ItemsContainer>
        {data.faqLists.map((item, index) => {
          const isOpen = expanded === index
          const isPrevToExpanded = expanded === index + 1
          return (
            <StyledAccordion
              key={index}
              className={isPrevToExpanded ? "prev-to-expanded" : undefined}
              square
              expanded={isOpen}
              onChange={handleChange(index)}
            >
              <StyledAccordionSummary
                expandIcon={isOpen ? <RemoveIcon /> : <AddIcon />}
                aria-controls={`faq-panel-${index}-content`}
                id={`faq-panel-${index}-header`}
              >
                <QuestionText $expanded={isOpen}>{item.question}</QuestionText>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <AnswerText>{item.answer}</AnswerText>
              </StyledAccordionDetails>
            </StyledAccordion>
          )
        })}
      </ItemsContainer>
    </Wrapper>
  )
}

export default FAQComp
